import { useState, useEffect } from "react";
import { Box, Link as MuiLink } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import { imagepathReturn } from "../../config/formatters.ts";

interface Certificate {
  id: number;
  student_name: string;
  admission_no: string;
  tc_no: string;
  tc_file: string;
  status: boolean;
}

export default function TransferCertificates() {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCertificates();
  }, [page, limit, search]);

  const getCertificates = async () => {
    setLoading(true);
    const response = await request.post("transfer-certificates", {
      search: search,
      limit,
      offset: page,
      orderColumn: "tc_no",
      orderDirection: "desc",
    });
    setCertificates(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "student_name",
      label: "Student Name",
      minWidth: 160,
      format: (value) => value,
    },
    {
      id: "admission_no",
      label: "Admission No.",
      minWidth: 140,
      format: (value) => value || "—",
    },
    {
      id: "tc_no",
      label: "TC No.",
      minWidth: 120,
      format: (value) => value || "—",
    },
    {
      id: "tc_file",
      label: "TC File",
      minWidth: 100,
      format: (value) =>
        value ? (
          <MuiLink href={imagepathReturn(value)} target="_blank" rel="noreferrer" underline="hover">
            <InsertDriveFileIcon sx={{ color: "#667eea" }} />
          </MuiLink>
        ) : (
          "—"
        ),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/transfer-certificates/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.student_name }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Transfer Certificates"
        showSearch
        searchPlaceholder="Search certificates..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add TC",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/transfer-certificates/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={certificates}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete TC - " + deleteDialog?.title}
          message="Are you sure you want to delete this transfer certificate? This action cannot be undone."
          apiEndpoint={"transfer-certificates/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getCertificates();
          }}
        />
      </PageWrapper>
    </>
  );
}